import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, FaCheckCircle } from 'react-icons/fa';
import { api } from '../App';
import { useLanguage } from '../LanguageContext';
import SEO from '../components/SEO';
import './Contact.css';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.post('/api/contact', formData);
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO 
        title={t('contact.seoTitle')}
        description={t('contact.seoDescription')}
        keywords="contact future money, crypto investment consultation, Ljubljana Slovenia, schedule meeting, investment inquiry"
      />
      
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <motion.div
            className="contact-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-subtitle">{t('contact.getInTouch')}</span>
            <h1 className="contact-title">
              {t('contact.heroTitle')}<br />
              <span className="gold-text">{t('contact.heroTitleGold')}</span>
            </h1>
            <p className="contact-description">
              {t('contact.heroDescription')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <motion.div
              className="contact-info"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>{t('contact.contactInfo')}</h2>
              <p>
                {t('contact.contactInfoDesc')}
              </p>

              <div className="info-items">
                <div className="info-item">
                  <div className="info-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="info-content">
                    <h4>{t('contact.officeAddress')}</h4>
                    <p>Dunajska cesta 101<br />1000 Ljubljana, Slovenia</p>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">
                    <FaEnvelope />
                  </div>
                  <div className="info-content">
                    <h4>{t('contact.email')}</h4>
                    <a href="mailto:katja.damij@tadadviser.com">katja.damij@tadadviser.com</a>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">
                    <FaPhone />
                  </div>
                  <div className="info-content">
                    <h4>{t('contact.phone')}</h4>
                    <a href="tel:+38641444001">+386 41 444 001</a>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">
                    <FaLinkedin />
                  </div>
                  <div className="info-content">
                    <h4>{t('contact.linkedin')}</h4>
                    <a href="https://www.linkedin.com/company/first-crypto-fund/" target="_blank" rel="noopener noreferrer">
                      {t('contact.followLinkedin')}
                    </a>
                  </div>
                </div>
              </div>

              {/* Booking CTA */}
              <div className="booking-card">
                <h3>{t('contact.scheduleConsultation')}</h3>
                <p>{t('contact.scheduleDesc')}</p>
                <a 
                  href="https://calendar.app.google/HApyWJxdUvwEQNLL6"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  {t('contact.bookNow')}
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              className="contact-form-container"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {success ? (
                <div className="success-message">
                  <FaCheckCircle />
                  <h3>{t('contact.messageSent')}</h3>
                  <p>{t('contact.thankYou')}</p>
                  <button 
                    className="btn btn-outline"
                    onClick={() => setSuccess(false)}
                  >
                    {t('contact.sendAnother')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <h2>{t('contact.sendMessage')}</h2>
                  
                  {error && (
                    <div className="form-error">
                      {error}
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('contact.name')} *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder={t('contact.yourName')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('contact.email')} *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder={t('contact.yourEmail')}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('contact.subject')}</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">{t('contact.selectTopic')}</option>
                      <option value="Investment Inquiry">{t('contact.investmentInquiry')}</option>
                      <option value="Fund Information">{t('contact.fundInformation')}</option>
                      <option value="Partnership">{t('contact.partnership')}</option>
                      <option value="General Question">{t('contact.generalQuestion')}</option>
                      <option value="Other">{t('contact.other')}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('contact.message')} *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="form-textarea"
                      placeholder={t('contact.howCanWeHelp')}
                      rows="5"
                      required
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary btn-full"
                    disabled={loading}
                  >
                    {loading ? t('contact.sending') : t('contact.sendMessageBtn')}
                  </button>

                  <p className="form-disclaimer">
                    {t('contact.formDisclaimer')}
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
